import { Box, styled } from "@material-ui/core";
import { isToolUIPart } from "ai";
import { Link } from "react-router-dom";
import {
  EPOCUS_BORDER,
  EPOCUS_PRIMARY,
  EPOCUS_WHITE,
  rem,
} from "../../_lib/epocus-palette";
import { useHasEpocusProgram } from "../../_lib/use-has-epocus-program";
import { linkToEpocusHome } from "../../links";
import { Composer } from "./components/composer";
import { ConnectionBanner } from "./components/connection-banner";
import { ConversationView } from "./components/conversation-view";
import { ProgressDots } from "./components/progress-dots";
import type { ChoiceOption } from "./onboarding-chat";
import { useOnboardingChat } from "./use-onboarding-chat";
import { useVoiceInput } from "./use-voice-input";

const Conversation = styled(Box)({
  flex: 1,
  minHeight: 0,
  display: "flex",
  flexDirection: "column",
  backgroundColor: EPOCUS_WHITE,
  border: `1px solid ${EPOCUS_BORDER}`,
  borderRadius: 16,
  overflow: "hidden",
});

const SubHeader = styled(Box)({
  display: "flex",
  alignItems: "center",
  justifyContent: "flex-end",
  padding: "8px 18px",
  flex: "none",
  borderBottom: `1px solid ${EPOCUS_BORDER}`,
});

const HomeCta = styled(Link)({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  margin: "0 18px 10px",
  minHeight: 44,
  borderRadius: 10,
  fontSize: rem(14),
  fontWeight: 700,
  color: EPOCUS_WHITE,
  backgroundColor: EPOCUS_PRIMARY,
  textDecoration: "none",
});

export const OnboardingConversation: FC<{ scenarioId: string }> = ({
  scenarioId,
}) => {
  const {
    messages,
    status,
    error,
    sendMessage,
    stop,
    resumeStream,
    addToolApprovalResponse,
    watchProgress,
    checkRoutingNow,
    progress,
    textInput,
  } = useOnboardingChat(scenarioId);
  const { hasProgram } = useHasEpocusProgram();
  const voice = useVoiceInput();

  const parts = messages.flatMap((message) => message.parts);
  const awaitingApproval = parts.some(
    (part) => isToolUIPart(part) && part.state === "approval-requested",
  );
  const lastMessage = messages.at(-1);
  const awaitingChoice =
    lastMessage?.role === "assistant" &&
    lastMessage.parts.some((part) => part.type === "data-choices");
  const awaitingScan = parts.some(
    (part) =>
      part.type === "data-watch" &&
      !(part.id ? watchProgress[part.id]?.outcome : undefined),
  );
  const agentOfferedHomeCta = parts.some(
    (part) =>
      part.type === "data-cta" && part.data.destination === linkToEpocusHome(),
  );

  const busy = status === "streaming" || status === "submitted";
  const respondToChoices = (options: ChoiceOption[]) =>
    void sendMessage({
      role: "user",
      parts: [
        {
          type: "text",
          text: options.map((option) => option.label).join(", "),
        },
        {
          type: "data-choice-response",
          data: { selected: options.map((option) => option.id) },
        },
      ],
    });

  return (
    <Conversation>
      {progress && (
        <SubHeader>
          <ProgressDots step={progress.step} total={progress.total} />
        </SubHeader>
      )}
      <ConnectionBanner error={error} onRetry={() => void resumeStream()} />
      <ConversationView
        messages={messages}
        thinking={status === "submitted"}
        streaming={busy}
        watchProgress={watchProgress}
        onRespondToApproval={(id, approved) =>
          void addToolApprovalResponse({ id, approved })
        }
        onRespondToChoices={respondToChoices}
        onCheckRoutingNow={checkRoutingNow}
      />
      {hasProgram && !agentOfferedHomeCta && (
        <HomeCta to={linkToEpocusHome()}>Go to ePOCUS home</HomeCta>
      )}
      <Composer
        textInput={textInput}
        busy={busy}
        awaitingApproval={awaitingApproval}
        awaitingChoice={Boolean(awaitingChoice)}
        awaitingScan={awaitingScan}
        voice={voice}
        onSend={(text) => void sendMessage({ text })}
        onStop={() => void stop()}
      />
    </Conversation>
  );
};
